import { useResponsiveValue } from '~/hooks/use-responsive-breakpoint';

/**
 * Custom hook to handle all responsive values for tabs component
 * Similar to horizontal list implementation
 */
export function useTabsResponsiveValues(styles: any) {
  const useDebounce = false;

  const stackingSwitch = useResponsiveValue(
    {
      mobile: styles?.tab_bar_stacking_switch_mobile,
      tablet: styles?.tab_bar_stacking_switch_tablet,
      desktop: styles?.tab_bar_stacking_switch_desktop,
      largeDesktop: styles?.tab_bar_stacking_switch_large_desktop,
    },
    useDebounce
  );

  const stickySwitch = useResponsiveValue(
    {
      mobile: styles?.tab_bar_sticky_switch_mobile,
      tablet: styles?.tab_bar_sticky_switch_tablet,
      desktop: styles?.tab_bar_sticky_switch_desktop,
      largeDesktop: styles?.tab_bar_sticky_switch_large_desktop,
    },
    useDebounce
  );

  const blendingSwitch = useResponsiveValue(
    {
      mobile: styles?.tab_bar_blending_switch_mobile,
      tablet: styles?.tab_bar_blending_switch_tablet,
      desktop: styles?.tab_bar_blending_switch_desktop,
      largeDesktop: styles?.tab_bar_blending_switch_large_desktop,
    },
    useDebounce
  );

  const arrowsSwitch = useResponsiveValue(
    {
      mobile: styles?.tab_bar_arrows_mobile_switch,
      tablet: styles?.tab_bar_arrows_tablet_switch,
      desktop: styles?.tab_bar_arrows_desktop_switch,
      largeDesktop: styles?.tab_bar_arrows_large_desktop_switch,
    },
    useDebounce
  );

  const maxWidthSwitch = useResponsiveValue(
    {
      mobile: styles?.tab_bar_max_width_switch_mobile,
      tablet: styles?.tab_bar_max_width_switch_tablet,
      desktop: styles?.tab_bar_max_width_switch_desktop,
      largeDesktop: styles?.tab_bar_max_width_switch_large_desktop,
    },
    useDebounce
  );

  const parentShadowSwitch = useResponsiveValue(
    {
      mobile: styles?.tab_bar_parent_shadow_switch_mobile,
      tablet: styles?.tab_bar_parent_shadow_switch_tablet,
      desktop: styles?.tab_bar_parent_shadow_switch_desktop,
      largeDesktop: styles?.tab_bar_parent_shadow_switch_large_desktop,
    },
    useDebounce
  );

  const parentBackgroundBlurSwitch = useResponsiveValue(
    {
      mobile: styles?.tab_bar_parent_background_blur_switch_mobile,
      tablet: styles?.tab_bar_parent_background_blur_switch_tablet,
      desktop: styles?.tab_bar_parent_background_blur_switch_desktop,
      largeDesktop: styles?.tab_bar_parent_background_blur_switch_large_desktop,
    },
    useDebounce
  );

  // Add gutter and padding values similar to horizontal list
  const gutter = useResponsiveValue(
    {
      mobile: styles?.tab_bar_horizontal_gutter_mobile,
      tablet: styles?.tab_bar_horizontal_gutter_tablet,
      desktop: styles?.tab_bar_horizontal_gutter_desktop,
      largeDesktop: styles?.tab_bar_horizontal_gutter_large_desktop,
    },
    useDebounce
  );

  const paddingLeft = useResponsiveValue(
    {
      mobile: styles?.tab_bar_mobile_padding_left,
      tablet: styles?.tab_bar_tablet_padding_left,
      desktop: styles?.tab_bar_desktop_padding_left,
      largeDesktop: styles?.tab_bar_large_desktop_padding_left,
    },
    useDebounce
  );

  const paddingRight = useResponsiveValue(
    {
      mobile: styles?.tab_bar_mobile_padding_right,
      tablet: styles?.tab_bar_tablet_padding_right,
      desktop: styles?.tab_bar_desktop_padding_right,
      largeDesktop: styles?.tab_bar_large_desktop_padding_right,
    },
    useDebounce
  );

  const componentMaxWidth = useResponsiveValue(
    {
      mobile: styles?.tab_bar_max_width_switch_mobile
        ? styles?.tab_bar_max_width_mobile
        : undefined,
      tablet: styles?.tab_bar_max_width_switch_tablet
        ? styles?.tab_bar_max_width_tablet
        : undefined,
      desktop: styles?.tab_bar_max_width_switch_desktop
        ? styles?.tab_bar_max_width_desktop
        : undefined,
      largeDesktop: styles?.tab_bar_max_width_switch_large_desktop
        ? styles?.tab_bar_max_width_large_desktop
        : undefined,
    },
    useDebounce
  );

  const tabsItemAlignment = useResponsiveValue(
    {
      mobile: styles?.tab_item_alignment_mobile,
      tablet: styles?.tab_item_alignment_tablet,
      desktop: styles?.tab_item_alignment_desktop,
      largeDesktop: styles?.tab_item_alignment_large_desktop,
    },
    useDebounce
  );

  const tabItemInnerAlignment = useResponsiveValue(
    {
      mobile: styles?.tab_item_inner_alignment_mobile,
      tablet: styles?.tab_item_inner_alignment_tablet,
      desktop: styles?.tab_item_inner_alignment_desktop,
      largeDesktop: styles?.tab_item_inner_alignment_large_desktop,
    },
    useDebounce
  );

  const tabItemSize = useResponsiveValue(
    {
      mobile: styles?.tab_item_size_select_mobile,
      tablet: styles?.tab_item_size_select_tablet,
      desktop: styles?.tab_item_size_select_desktop,
      largeDesktop: styles?.tab_item_size_select_large_desktop,
    },
    useDebounce
  );

  const borderParentContainer = useResponsiveValue(
    {
      mobile: styles?.tab_bar_parent_border_style_mobile,
      tablet: styles?.tab_bar_parent_border_style_tablet,
      desktop: styles?.tab_bar_parent_border_style_desktop,
      largeDesktop: styles?.tab_bar_parent_border_style_large_desktop,
    },
    useDebounce
  );

  const borderTabsContainer = useResponsiveValue(
    {
      mobile: styles?.tab_bar_border_style_mobile,
      tablet: styles?.tab_bar_border_style_tablet,
      desktop: styles?.tab_bar_border_style_desktop,
      largeDesktop: styles?.tab_bar_border_style_large_desktop,
    },
    useDebounce
  );

  const shadowSwitch = useResponsiveValue(
    {
      mobile: styles?.tab_bar_shadow_switch_mobile,
      tablet: styles?.tab_bar_shadow_switch_tablet,
      desktop: styles?.tab_bar_shadow_switch_desktop,
      largeDesktop: styles?.tab_bar_shadow_switch_large_desktop,
    },
    useDebounce
  );

  const backgroundBlurSwitch = useResponsiveValue(
    {
      mobile: styles?.tab_bar_background_blur_switch_mobile,
      tablet: styles?.tab_bar_background_blur_switch_tablet,
      desktop: styles?.tab_bar_background_blur_switch_desktop,
      largeDesktop: styles?.tab_bar_background_blur_switch_large_desktop,
    },
    useDebounce
  );

  return {
    stackingSwitch,
    stickySwitch,
    arrowsSwitch,
    blendingSwitch,
    shadowSwitch,
    parentShadowSwitch,
    gutter,
    paddingLeft,
    paddingRight,
    componentMaxWidth,
    tabsItemAlignment,
    tabItemInnerAlignment,
    tabItemSize,
    maxWidthSwitch,
    borderParentContainer,
    borderTabsContainer,
    backgroundBlurSwitch,
    parentBackgroundBlurSwitch,
  };
}
